/**
 * Nutrition form state <-> nutrition service payloads. Shared by the
 * product editor (add / update) and the nutrition worklist.
 */

/** Must match nutrition/src/engine (micronutrients.ts, additives.ts). */
export const MICRONUTRIENTS = [
  ["vitaminA", "Vitamin A", "µg"],
  ["vitaminD", "Vitamin D", "µg"],
  ["vitaminE", "Vitamin E", "mg"],
  ["vitaminC", "Vitamin C", "mg"],
  ["thiamine", "Thiamine (B1)", "mg"],
  ["riboflavin", "Riboflavin (B2)", "mg"],
  ["niacin", "Niacin (B3)", "mg"],
  ["vitaminB6", "Vitamin B6", "mg"],
  ["folate", "Folate", "µg"],
  ["vitaminB12", "Vitamin B12", "µg"],
  ["vitaminK", "Vitamin K", "µg"],
  ["pantothenicAcid", "Pantothenic acid (B5)", "mg"],
  ["biotin", "Biotin (B7)", "µg"],
  ["choline", "Choline", "mg"],
  ["calcium", "Calcium", "mg"],
  ["iron", "Iron", "mg"],
  ["zinc", "Zinc", "mg"],
  ["magnesium", "Magnesium", "mg"],
  ["potassium", "Potassium", "mg"],
  ["phosphorus", "Phosphorus", "mg"],
  ["iodine", "Iodine", "µg"],
  ["selenium", "Selenium", "µg"],
  ["copper", "Copper", "mg"],
  ["manganese", "Manganese", "mg"],
];

export const ALLERGENS = [
  ["GLUTEN", "Gluten (wheat, maida...)"],
  ["MILK", "Milk"],
  ["EGG", "Egg"],
  ["PEANUT", "Peanut"],
  ["TREE_NUT", "Tree nuts"],
  ["SOY", "Soy"],
  ["FISH", "Fish"],
  ["CRUSTACEAN", "Crustaceans"],
  ["SULPHITE", "Sulphite"],
];

export const PROTEIN_SOURCES = [
  ["", "Not set"],
  ["DAIRY", "Dairy (milk, paneer, whey)"],
  ["EGG", "Egg"],
  ["MEAT_FISH", "Meat / fish"],
  ["SOY", "Soy"],
  ["LEGUME", "Pulses / legumes"],
  ["CEREAL", "Cereals / grains"],
  ["NUT_SEED", "Nuts / seeds"],
  ["MIXED_PLANT", "Mixed plant (dal + rice...)"],
  ["NONE", "No meaningful protein"],
];

/** Selects category-specific score rules (nutrition/src/engine/food-category.ts). */
export const FOOD_CATEGORIES = [
  ["", "Auto (from store category)"],
  ["FRUIT", "Fruit"],
  ["VEGETABLE", "Vegetable"],
  ["LEGUME", "Pulses & legumes"],
  ["WHOLE_GRAIN", "Whole grain / millet"],
  ["REFINED_GRAIN", "Refined grain (white rice, maida)"],
  ["DAIRY", "Dairy (milk, curd, paneer)"],
  ["EGG", "Egg"],
  ["MEAT", "Meat & poultry"],
  ["FISH", "Fish & seafood"],
  ["NUT_SEED", "Nuts & seeds"],
  ["OIL_FAT", "Oil, ghee & fat"],
  ["BEVERAGE", "Beverage"],
  ["SNACK", "Snack (chips, namkeen)"],
  ["BAKERY", "Biscuit & bakery"],
  ["BREAKFAST_CEREAL", "Breakfast cereal"],
  ["READY_MEAL", "Ready meal / instant noodles"],
  ["PROCESSED_MEAT", "Processed meat (sausage, salami)"],
  ["SWEET", "Sweet & confectionery"],
  ["SUPPLEMENT", "Supplement / protein powder"],
];

/** How the product is sold; decides which database entries can describe it. */
export const PRODUCT_FORMS = [
  ["", "Auto (from store category)"],
  ["LOOSE", "Loose raw food (vegetables, counter meat, dal)"],
  ["FRESH_PREPARED", "Made fresh in the shop (fresh juice, dish)"],
  ["PACKAGED", "Packaged product (brand + label)"],
];

export const SOURCES = [
  ["PRODUCT_LABEL", "Pack label"],
  ["MANUAL", "Manual entry"],
  ["LAB_TESTED", "Lab tested"],
  ["OPEN_FOOD_FACTS", "Open Food Facts"],
  ["USDA", "USDA FoodData Central"],
  ["FATSECRET", "FatSecret"],
  ["ICMR_NIN", "ICMR-NIN (IFCT)"],
];

export const SOURCE_NAMES = {
  OPEN_FOOD_FACTS: "Open Food Facts",
  USDA: "USDA FoodData Central",
  FATSECRET: "FatSecret",
  LEGACY_IMPORT: "old admin data",
  MANUAL: "manual entry",
  PRODUCT_LABEL: "the pack label",
  LAB_TESTED: "a lab test",
  ICMR_NIN: "ICMR-NIN",
  COMBO_CONTENTS: "the combo contents",
  CALCULATED: "calculated",
};

export const EMPTY = {
  basisGrams: "100",
  servingGrams: "",
  isBeverage: false,
  calories: "",
  protein: "",
  carbs: "",
  fat: "",
  fiber: "",
  sugar: "",
  addedSugar: "",
  saturatedFat: "",
  transFat: "",
  sodium: "",
  cholesterol: "",
  ingredients: "",
  allergens: [],
  extraAdditives: "",
  novaGroup: "",
  nutriScoreDeclared: "",
  fruitVegLegumePct: "",
  proteinSource: "",
  pdcaas: "",
  foodCategory: "",
  productForm: "",
  dietType: "",
  brandName: "",
  barcode: "",
  source: "MANUAL",
  isVerified: false,
  micronutrients: {},
  // Set when the values came from an external database match
  sourceRecordId: null,
  matchConfidence: null,
  // Other entries that filled the match's gaps: [{ recordId, confidence }]
  fills: [],
  // Field (as the server names it: "sodium", "micronutrients.iron") -> { kind: "fill" | "calc", note }
  // for values not from the match itself; dropped when the admin edits the field.
  origin: {},
};

const str = (v) => (v === null || v === undefined ? "" : String(v));
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const legacyNum = (v) =>
  v === "" || v === null || v === undefined || isNaN(Number(v)) || Number(v) === 0 ? "" : String(Number(v));

/** Stored profile (GET /nutrition/admin/products/:id) -> form. */
export function fromProfile(p, facts) {
  return {
    ...EMPTY,
    basisGrams: str(p.basisGrams),
    servingGrams: str(p.servingGrams),
    isBeverage: p.isBeverage,
    calories: str(p.calories),
    protein: str(p.protein),
    carbs: str(p.carbs),
    fat: str(p.fat),
    fiber: str(p.fiber),
    sugar: str(p.sugar),
    addedSugar: str(p.addedSugar),
    saturatedFat: str(p.saturatedFat),
    transFat: str(p.transFat),
    sodium: str(p.sodium),
    cholesterol: str(p.cholesterol),
    ingredients: str(p.ingredients),
    allergens: facts?.declaredAllergens ?? p.allergens ?? [],
    extraAdditives: (facts?.extraAdditives ?? []).join(", "),
    novaGroup: str(p.novaGroup),
    nutriScoreDeclared: str(p.nutriScoreDeclared),
    fruitVegLegumePct: str(p.fruitVegLegumePct),
    proteinSource: str(p.proteinSource),
    pdcaas: str(p.pdcaas),
    foodCategory: str(p.foodCategory),
    productForm: str(p.productForm),
    dietType: str(p.dietType),
    brandName: str(p.brandName),
    barcode: str(p.barcode),
    // Edited by hand from here on (e.g. a combo no longer calculated from its contents).
    source: p.source === "LEGACY_IMPORT" || p.source === "COMBO_CONTENTS" ? "MANUAL" : p.source,
    isVerified: p.isVerified,
    micronutrients: Object.fromEntries(Object.entries(p.micronutrients ?? {}).map(([k, v]) => [k, str(v)])),
    sourceRecordId: p.sourceRecordId ?? null,
    matchConfidence: p.matchConfidence ?? null,
  };
}

/** Legacy product macros (cal_grm...) -> form, for products with no profile. */
export function fromLegacy(product) {
  return {
    ...EMPTY,
    calories: legacyNum(product?.cal_grm),
    protein: legacyNum(product?.protein_grm),
    carbs: legacyNum(product?.carbs_grm),
    fat: legacyNum(product?.fat_grm),
  };
}

/**
 * An external match (Open Food Facts / USDA / FatSecret candidate) -> form. Every label
 * field is replaced, so values from different products never get mixed;
 * the barcode and brand typed by the admin are kept.
 */
export function fromCandidate(form, candidate, complete = null) {
  const v = complete?.values ?? candidate.values;
  return {
    ...EMPTY,
    basisGrams: "100",
    servingGrams: str(v.servingGrams) || form.servingGrams,
    isBeverage: !!v.label.isBeverage,
    calories: str(v.calories),
    protein: str(v.protein),
    carbs: str(v.carbs),
    fat: str(v.fat),
    fiber: str(v.fiber),
    sugar: str(v.sugar),
    addedSugar: str(v.label.addedSugar),
    saturatedFat: str(v.label.saturatedFat),
    transFat: str(v.label.transFat),
    sodium: str(v.label.sodium),
    cholesterol: str(v.label.cholesterol),
    ingredients: str(v.label.ingredients),
    allergens: v.label.allergens ?? [],
    extraAdditives: (v.label.additives ?? []).join(", "),
    novaGroup: str(v.label.novaGroup),
    nutriScoreDeclared: str(v.label.nutriScoreDeclared),
    proteinSource: str(v.label.proteinSource),
    foodCategory: str(v.label.foodCategory),
    dietType: form.dietType,
    barcode: form.barcode || (candidate.byBarcode ? str(v.barcode) : ""),
    brandName: form.brandName || (candidate.byBarcode ? str(v.brandName) : ""),
    source: candidate.source,
    isVerified: false,
    micronutrients: Object.fromEntries(Object.entries(v.label.micronutrients ?? {}).map(([k, n]) => [k, str(n)])),
    sourceRecordId: candidate.recordId,
    matchConfidence: candidate.confidence,
    ...(complete ? originOf(complete) : { fills: [], origin: {} }),
  };
}

/** Lookup `complete` -> which values came from other entries or were calculated. */
function originOf(complete) {
  const names = Object.fromEntries(complete.fills.map((f) => [f.candidate.recordId, SOURCE_NAMES[f.candidate.source]]));
  const origin = {};
  for (const [field, from] of Object.entries(complete.provenance)) {
    if (from.source === "CALCULATED") origin[field] = { kind: "calc", note: complete.calculated[field] };
    else if (names[from.recordId]) origin[field] = { kind: "fill", note: `from ${names[from.recordId]}` };
  }
  return {
    fills: complete.fills.map((f) => ({ recordId: f.candidate.recordId, confidence: f.candidate.confidence })),
    origin,
  };
}

/**
 * An estimate added up from the ingredients -> form. The ingredient text and
 * what the admin set about the product stay; every value is calculated.
 */
export function fromEstimate(form, estimate) {
  const v = estimate.values;
  const next = fromCandidate(form, { values: v, source: "MANUAL", recordId: null, confidence: null, byBarcode: false });
  return {
    ...next,
    ingredients: form.ingredients,
    foodCategory: form.foodCategory,
    productForm: form.productForm,
    dietType: form.dietType,
    source: "MANUAL",
    sourceRecordId: null,
    matchConfidence: null,
    fills: [],
    origin: Object.fromEntries(Object.entries(estimate.calculated).map(([f, note]) => [f, { kind: "calc", note }])),
  };
}

/** Form keys -> the server's field names, for `origin`. */
const ORIGIN_KEY = { extraAdditives: "additives" };

/** The form after the admin edits `updates`: those values are now the admin's own. */
export function withEdits(form, updates) {
  const edited = Object.keys(updates).flatMap((k) =>
    k === "micronutrients"
      ? Object.keys(updates.micronutrients)
          .filter((c) => updates.micronutrients[c] !== form.micronutrients[c])
          .map((c) => `micronutrients.${c}`)
      : [ORIGIN_KEY[k] ?? k],
  );
  const origin = Object.fromEntries(Object.entries(form.origin ?? {}).filter(([f]) => !edited.includes(f)));
  return { ...form, ...updates, origin };
}

/** Values no 100 g of food can have: more than 100 g of macros or 900 kcal. */
function exceedsHundredGrams(f, basisGrams) {
  const per = 100 / basisGrams;
  const macros = ((Number(f.protein) || 0) + (Number(f.carbs) || 0) + (Number(f.fat) || 0)) * per;
  return macros > 100.5 || (Number(f.calories) || 0) * per > 900;
}

/** The form's values cannot be per its "Values are per" weight. */
export const valuesImpossibleForBasis = (f) => exceedsHundredGrams(f, Number(f.basisGrams) || 100);

// Kit contents ("Chicken Breast 500g , Eggs 1 Tray"), as nutrition/src/engine/kit-contents.ts.
const GRAMS = { g: 1, gm: 1, gms: 1, gram: 1, grams: 1, kg: 1000, kgs: 1000, ml: 1, l: 1000, ltr: 1000, litre: 1000, liter: 1000 };
const EGG_GRAMS = 50;
const WEIGHT = /(\d+(?:\.\d+)?)\s*(kgs?|gms?|grams?|g|ml|ltr|litre|liter|l)\b/i;
const TIMES = /\(\s*(\d+)\s*(?:nos?|pcs|packs?)\s*\)/i;
const COUNT = /(\d+(?:\.\d+)?)\s*(nos?|pcs|pieces?|dozens?|trays?|traes)\b/i;
// Restaurant portions ("White Rice 2Full") and counted dishes ("Chapati 8")
const PORTION_GRAMS = { full: 500, half: 250, plate: 500 };
const PIECE_GRAMS = [
  [/\b(chapat(i|hi)s?|rotis?|phulkas?)\b/i, 40],
  [/\bpesarattus?\b/i, 100],
  [/\b(parathas?|parottas?)\b/i, 80],
  [/\bidl(i|y|is)\b/i, 40],
  [/\bdosas?\b/i, 100],
  [/\b(puris?|pooris?)\b/i, 25],
  [/\b(vadas?|vadais?)\b/i, 50],
];
const PORTION = /(\d+(?:\.\d+)?)\s*(full|half|plates?)\b/i;
// One serving of rice / grain dishes ("Pongal 4 Serving")
const SERVING_DISH = /pongal|upma|poha|khichdi|kichdi|uggani|bisi\s*bele|curd\s*rice|lemon\s*rice|pulihora|tamarind\s*rice/i;
const SERVING_GRAMS = 200;
const SERVINGS = /(\d+(?:\.\d+)?)\s*(servings?|bowls?|cups?)\b/i;
const BARE_COUNT = /(\d+(?:\.\d+)?)\s*$/;

function itemGrams(text) {
  const weight = WEIGHT.exec(text);
  if (weight) {
    const times = TIMES.exec(text.slice(weight.index));
    return Number(weight[1]) * GRAMS[weight[2].toLowerCase()] * (times ? Number(times[1]) : 1);
  }
  const count = COUNT.exec(text);
  if (count && /\beggs?\b/i.test(text)) {
    const unit = count[2].toLowerCase();
    const eggs = unit.startsWith("tra") ? 30 : unit.startsWith("dozen") ? 12 : 1;
    return Number(count[1]) * eggs * EGG_GRAMS;
  }
  const portion = PORTION.exec(text);
  if (portion) return Number(portion[1]) * PORTION_GRAMS[portion[2].toLowerCase().replace(/s$/, "")];
  const servings = SERVINGS.exec(text);
  if (servings && SERVING_DISH.test(text)) return Number(servings[1]) * SERVING_GRAMS;
  const piece = PIECE_GRAMS.find(([name]) => name.test(text));
  const pieces = count ?? BARE_COUNT.exec(text);
  if (piece && pieces) return Number(pieces[1]) * piece[1];
  return null;
}

/** The items of a kit's contents list, with their weight when known. */
export function kitItems(description) {
  return String(description ?? "")
    .replace(/<[^>]*>/g, " ")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((name) => ({ name, grams: itemGrams(name) }));
}

/** A kit or combo pack: named so, with at least two items listed in the description. */
export const isKitProduct = (name, description) =>
  /\b(kits?|packs?|combo)\b/i.test(String(name ?? "")) && kitItems(description).length >= 2;

/**
 * Smallest weight the values can be for: no 100 g holds more than 900 kcal
 * or 100 g of protein + carbs + fat.
 */
export function minimumBasisGrams(f) {
  const macros = (Number(f.protein) || 0) + (Number(f.carbs) || 0) + (Number(f.fat) || 0);
  return Math.ceil(Math.max(((Number(f.calories) || 0) * 100) / 900, (macros * 100) / 100.5, 1));
}

/** Total weight of a kit from its contents list, or null unless every item can be weighed. */
export function kitGrams(description) {
  const items = kitItems(description);
  if (items.length === 0 || items.some((i) => i.grams === null)) return null;
  return Math.round(items.reduce((sum, i) => sum + i.grams, 0));
}

/**
 * For values that are the totals of a whole kit: the kit weight they refer
 * to, read from its contents. Null when the values fit the current basis,
 * or the kit cannot be weighed, or they are impossible even for the kit.
 */
export function kitTotalsBasis(f, description) {
  if (!valuesImpossibleForBasis(f)) return null;
  const grams = kitGrams(description);
  return grams && !exceedsHundredGrams(f, grams) ? grams : null;
}

/**
 * For values that are the totals of a whole pack: the pack weight from the
 * product name ("Britannia Good Day 200g", "Whey Protein 1kg"), when the
 * values fit it. Null otherwise.
 */
export function packWeightFromName(f, name) {
  const weight = WEIGHT.exec(String(name ?? "").split("\n")[0]);
  if (!weight || !valuesImpossibleForBasis(f)) return null;
  const grams = Math.round(Number(weight[1]) * GRAMS[weight[2].toLowerCase()]);
  return grams > 100 && !exceedsHundredGrams(f, grams) ? grams : null;
}

/** Values entered for a whole pack (more than 100 g): the same values per 100 g. */
export function per100Values(f) {
  const scale = 100 / (Number(f.basisGrams) || 100);
  const at = (v) => (v === "" || isNaN(Number(v)) ? null : Math.round(Number(v) * scale * 10) / 10);
  return { calories: at(f.calories), protein: at(f.protein), carbs: at(f.carbs), fat: at(f.fat), sugar: at(f.sugar) };
}

export const macrosValid = (f) =>
  ["calories", "protein", "carbs", "fat"].every((k) => f[k] !== "" && !isNaN(Number(f[k])));

/** Grade-relevant facts still empty. */
export const missingForGrade = (f) =>
  [
    f.sugar === "" && "sugars",
    f.saturatedFat === "" && "saturated fat",
    f.sodium === "" && "sodium",
  ].filter(Boolean);

/** Body for POST /nutrition/score/preview. */
export function toPreviewBody(f) {
  return {
    basisGrams: num(f.basisGrams) ?? 100,
    calories: num(f.calories),
    protein: num(f.protein),
    carbs: num(f.carbs),
    fat: num(f.fat),
    fiber: num(f.fiber),
    sugar: num(f.sugar),
    servingGrams: num(f.servingGrams),
    dietType: f.dietType || null,
    isVerified: f.isVerified,
    // Sets the confidence level shown in the preview
    source: f.source,
    label: {
      saturatedFat: num(f.saturatedFat),
      transFat: num(f.transFat),
      addedSugar: num(f.addedSugar),
      sodium: num(f.sodium),
      cholesterol: num(f.cholesterol),
      micronutrients: Object.fromEntries(
        Object.entries(f.micronutrients)
          .filter(([, v]) => v !== "")
          .map(([k, v]) => [k, Number(v)]),
      ),
      ingredients: f.ingredients.trim() || null,
      allergens: f.allergens,
      additives: f.extraAdditives
        .split(",")
        .map((a) => a.trim())
        .filter(Boolean),
      novaGroup: num(f.novaGroup),
      nutriScoreDeclared: f.nutriScoreDeclared || null,
      fruitVegLegumePct: num(f.fruitVegLegumePct),
      isBeverage: f.isBeverage,
      proteinSource: f.proteinSource || null,
      pdcaas: num(f.pdcaas),
      foodCategory: f.foodCategory || null,
      productForm: f.productForm || null,
    },
  };
}

/** Body for PUT /nutrition/products/:id. */
export function toSaveBody(f, productBasics) {
  return {
    ...toPreviewBody(f),
    source: f.source,
    brandName: f.brandName || null,
    barcode: f.barcode || null,
    sourceRecordId: f.sourceRecordId ?? undefined,
    matchConfidence: f.matchConfidence ?? undefined,
    // Filled entries and calculated values the admin left as they were keep that origin.
    fills: f.fills?.length ? f.fills : undefined,
    calculatedFields: Object.entries(f.origin ?? {})
      .filter(([, o]) => o.kind === "calc")
      .map(([field]) => field),
    ...(productBasics && { product: productBasics }),
  };
}

/**
 * Macro fields the legacy product API and the current customer app use.
 * Percentages are shares of energy, like the old form's summary line.
 */
export function legacyMacros(f) {
  const p = Number(f.protein) || 0;
  const c = Number(f.carbs) || 0;
  const fat = Number(f.fat) || 0;
  const kcal = p * 4 + c * 4 + fat * 9 || 1;
  const pct = (v) => String(Math.round(v * 10) / 10);
  const scale = 100 / (Number(f.basisGrams) || 100);
  const per100 = (v) => (v === "" ? "0" : String(Math.round(Number(v) * scale * 100) / 100));
  return {
    cal_grm: per100(f.calories),
    protein_grm: per100(f.protein),
    carbs_grm: per100(f.carbs),
    fat_grm: per100(f.fat),
    protein_percent: pct((p * 4 * 100) / kcal),
    carbs_percent: pct((c * 4 * 100) / kcal),
    fat_percent: pct((fat * 9 * 100) / kcal),
  };
}
